import { FC } from 'react';

import { Button, Empty, FileIconLandscape, Image, Typography } from '~shared/ui';
import { useTranslation } from '~shared/lib/i18n/i18n';
import defaultPhoto from '~shared/assets/defaultAvatar.svg';
import { timezoneDayjs } from '~shared/lib/utils/dayjs';

import { IProfile } from '../../model/types';

const ApiUrl = import.meta.env.VITE_API_URL;
export interface ProfileDetailViewProps {
  profile: IProfile | null;
  editSlot: React.ReactNode;
}

export const ProfileDetailView: FC<ProfileDetailViewProps> = ({ profile, editSlot }) => {
  const { t } = useTranslation();

  if (!profile) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div className="grid gap-10">
      <div className="rounded-[100px] overflow-hidden w-fit">
        <Image
          fallback={defaultPhoto}
          height={100}
          width={100}
          src={`${ApiUrl}/kindergarten/profile/photo/sm/${
            profile?.accelerator_foto
          }?t=${Date.now()}`}
          preview={{
            src: `${ApiUrl}/kindergarten/profile/photo/lg/${
              profile?.accelerator_foto
            }?t=${Date.now()}`,
          }}
        />
      </div>
      <div className="grid grid-cols-2 gap-5 justify-between xs:grid-cols-1">
        <Typography.Text className="grid gap-2">
          <span className="text-[12px] text-placeholder font-[600]"> {t('profile:fio')}</span>
          {`${profile.surname} ${profile.name} ${profile.patronymic ? profile.patronymic : ''}`}
        </Typography.Text>

        <Typography.Text className="grid gap-2">
          <span className="text-[12px] text-placeholder font-[600]"> {t('profile:pin')}</span>
          {profile.pin}
        </Typography.Text>

        <Typography.Text className="grid gap-2">
          <span className="text-[12px] text-placeholder font-[600]"> {t('profile:bdate')}</span>
          {timezoneDayjs(profile.birth_date).format('DD.MM.YYYY')}
        </Typography.Text>

        <Typography.Text className="grid gap-2">
          <span className="text-[12px] text-placeholder font-[600]"> {t('profile:passport')}</span>
          {`${profile.passport_series} ${profile.passport_number}`}
        </Typography.Text>

        <Typography.Text className="grid gap-2">
          <span className="text-[12px] text-placeholder font-[600]"> {t('profile:telephone')}</span>
          {profile.telephone}
        </Typography.Text>

        <Typography.Text className="grid gap-2">
          <span className="text-[12px] text-placeholder font-[600]"> {t('profile:email')}</span>
          {profile.email}
        </Typography.Text>

        {profile.accelerator_docs?.length ? (
          <div className="grid gap-5 mt-5">
            {profile.accelerator_docs.map((docs) => {
              return (
                <Button
                  type="link"
                  className="flex gap-2 items-center p-0 text-black"
                  key={docs.name}
                  href={`${ApiUrl}/kindergarten/profile/docs/${docs.name}`}
                  target="_blank"
                  rel="noreferrer"
                  icon={<FileIconLandscape stroke="var(--placeholder)" />}
                >
                  {t(`docs.${docs.type}`)}
                </Button>
              );
            })}
          </div>
        ) : null}
      </div>
      {editSlot}
    </div>
  );
};
